import React, { useState, useEffect, useRef } from "react";
import styled from "styled-components";
import Navbar from "../Components/Navbar";
import useUsers from "../hooks/Admin/useGetUsers";
import profileIcon from "../Assets/profileicon.png";

const Profile = () => {
  const localUserData = JSON.parse(localStorage.getItem("userData")) || {};
  const { users, loading, error } = useUsers();

  const userFromServer = users.find(
    (u) => u.email === localUserData.email
  );

  const [name, setName] = useState("");
  const [image, setImage] = useState(profileIcon);
  const [selectedFile, setSelectedFile] = useState(null);
  const [isSaving, setIsSaving] = useState(false);
  const [message, setMessage] = useState({ type: "", text: "" });

  const fileInputRef = useRef(null);

  useEffect(() => {
    if (userFromServer) {
      setName(userFromServer.fullname || "");
      setImage(userFromServer.profilePicture || profileIcon);
    }
  }, [userFromServer]);

  // ✅ SHOW MESSAGE
  const showMessage = (type, text) => {
    setMessage({ type, text });
    setTimeout(() => setMessage({ type: "", text: "" }), 3000);
  };

  // ✅ HANDLE IMAGE SELECT
  const handleImageClick = () => {
    fileInputRef.current.click();
  };

  const handleImageChange = (e) => {
    const file = e.target.files[0];

    if (!file) return;

    // validation
    if (!file.type.startsWith("image/")) {
      showMessage("error", "Please select a valid image file");
      return;
    }

    if (file.size > 1024 * 1024) {
      showMessage("error", "File must be less than 1MB");
      return;
    }

    const reader = new FileReader();
    reader.onloadend = () => {
      setImage(reader.result); // preview
      setSelectedFile(file); // store file
    };
    reader.readAsDataURL(file);
  };

  // ✅ HANDLE SAVE CHANGES
  const handleSaveChanges = async () => {
    setIsSaving(true);

    try {
      const formData = new FormData();
      formData.append("fullname", name);
      formData.append("email", localUserData.email);

      if (selectedFile) {
        formData.append("profilePicture", selectedFile);
      }

      // Send to backend
      const response = await fetch("/api/profile/update", {
        method: "POST",
        body: formData,
        headers: {
          Accept: "application/json",
        },
      });

      if (!response.ok) {
        throw new Error("Failed to save profile");
      }

      const result = await response.json();

      // Update localStorage
      const updatedUserData = {
        ...localUserData,
        fullname: name,
        profilePicture: image,
      };
      localStorage.setItem("userData", JSON.stringify(updatedUserData));

      showMessage("success", "Profile updated successfully!");
      setSelectedFile(null); // clear selected file
    } catch (err) {
      console.error("Error saving profile:", err);
      showMessage("error", "Failed to save profile. Please try again.");
    } finally {
      setIsSaving(false);
    }
  };

  if (loading) return <div>Loading profile...</div>;
  if (error) return <div>Error: {error}</div>;

  return (
    <>
      <Navbar />
      <Wrapper>
        <Card>
          <Header>
            <Title>My Profile</Title>
            <Subtitle>
              Manage and protect your account information
            </Subtitle>
          </Header>

          {/* MESSAGE DISPLAY */}
          {message.text && (
            <MessageBox type={message.type}>
              {message.type === "success" ? "✓" : "✕"} {message.text}
            </MessageBox>
          )}

          <Content>
            {/* LEFT */}
            <Left>
              <FormGroup>
                <Label>Full Name</Label>
                <Input
                  value={name}
                  onChange={(e) => setName(e.target.value)}
                />
              </FormGroup>

              <FormGroup>
                <Label>Email Address</Label>
                <StaticText>
                  {userFromServer?.email || "N/A"}
                  <ChangeLink>Change</ChangeLink>
                </StaticText>
              </FormGroup>

              <FormGroup>
                <Label>Phone Number</Label>
                <StaticText>
                  {userFromServer?.contact || "N/A"}
                  <ChangeLink>Change</ChangeLink>
                </StaticText>
              </FormGroup>

              <SaveButton onClick={handleSaveChanges} disabled={isSaving}>
                {isSaving ? "Saving..." : "Save Changes"}
              </SaveButton>
            </Left>

            <Divider />

            {/* RIGHT */}
            <Right>
              <ProfileSection>
                <ProfileImage src={image} alt="Profile" />

                <UploadButton onClick={handleImageClick}>
                  Select Image
                </UploadButton>

                {/* hidden input */}
                <HiddenInput
                  type="file"
                  accept="image/png, image/jpeg"
                  ref={fileInputRef}
                  onChange={handleImageChange}
                />

                <InfoBox>
                  <InfoItem>Recommended: Square (500x500)</InfoItem>
                  <InfoItem>Max size: 1MB</InfoItem>
                  <InfoItem>Formats: JPG, PNG</InfoItem>
                </InfoBox>
              </ProfileSection>
            </Right>
          </Content>
        </Card>
      </Wrapper>
    </>
  );
};

export default Profile;

/* ================= STYLES ================= */

const Wrapper = styled.div`
  padding: 60px 20px;
  background: linear-gradient(135deg, #eef6f0, #e3efe6);
  min-height: 100vh;
`;

const Card = styled.div`
  max-width: 1100px;
  margin: auto;
  background: #fff;
  border-radius: 16px;
  padding: 40px;
  box-shadow: 0 10px 30px rgba(0, 0, 0, 0.08);
`;

const MessageBox = styled.div`
  padding: 12px 16px;
  border-radius: 10px;
  margin-bottom: 20px;
  font-weight: 600;
  animation: slideIn 0.3s ease;

  @keyframes slideIn {
    from {
      opacity: 0;
      transform: translateY(-10px);
    }
    to {
      opacity: 1;
      transform: translateY(0);
    }
  }

  ${(props) =>
    props.type === "success"
      ? `
    background: #d4edda;
    color: #155724;
    border: 1px solid #c3e6cb;
  `
      : `
    background: #f8d7da;
    color: #721c24;
    border: 1px solid #f5c6cb;
  `}
`;

const Header = styled.div`
  margin-bottom: 30px;
`;

const Title = styled.h2`
  font-size: 30px;
  font-weight: 700;
  color: #1b4332;
`;

const Subtitle = styled.p`
  color: #6c8f7a;
  margin-top: 5px;
`;

const Content = styled.div`
  display: flex;
  gap: 40px;
`;

const Divider = styled.div`
  width: 1px;
  background: #e5e7eb;
`;

const Left = styled.div`
  flex: 1;
`;

const Right = styled.div`
  width: 260px;
  display: flex;
  justify-content: center; /* ✅ CENTER HORIZONTALLY */
`;

const ProfileSection = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center; /* ✅ CENTER EVERYTHING */
  text-align: center;
`;

const FormGroup = styled.div`
  margin-bottom: 20px;
`;

const Label = styled.label`
  font-size: 13px;
  font-weight: 600;
  color: #2d6a4f;
  margin-bottom: 6px;
  display: block;
`;

const Input = styled.input`
  width: 100%;
  padding: 12px;
  border-radius: 10px;
  border: 1px solid #d1e7d6;
  background: #f8fcf9;

  &:focus {
    border-color: #40916c;
    box-shadow: 0 0 0 2px rgba(64, 145, 108, 0.2);
    outline: none;
  }
`;

const StaticText = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  background: #f1f8f4;
  padding: 12px;
  border-radius: 10px;
`;

const ChangeLink = styled.span`
  color: #40916c;
  cursor: pointer;

  &:hover {
    text-decoration: underline;
  }
`;

const ProfileImage = styled.img`
  width: 130px;
  height: 130px;
  border-radius: 50%;
  object-fit: cover;
  border: 4px solid #d8f3dc;
  margin-bottom: 15px;
`;

const UploadButton = styled.button`
  background: #40916c;
  color: white;
  padding: 10px 20px;
  border-radius: 10px;
  border: none;
  cursor: pointer;
  margin-bottom: 15px;

  &:hover {
    background: #2d6a4f;
  }
`;

const HiddenInput = styled.input`
  display: none;
`;

const InfoBox = styled.div`
  background: #f6fbf7;
  border-radius: 10px;
  padding: 15px;
  width: 100%;
`;

const InfoItem = styled.p`
  font-size: 13px;
  color: #5f7f6e;
  margin-bottom: 6px;
`;

const SaveButton = styled.button`
  margin-top: 10px;
  background: #2d6a4f;
  color: white;
  padding: 12px 24px;
  border-radius: 10px;
  border: none;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.3s ease;

  &:hover:not(:disabled) {
    background: #1b4332;
  }

  &:disabled {
    opacity: 0.6;
    cursor: not-allowed;
  }
`;